import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrailMap = ({ trailData, connectedPoints, onPointClick }) => {
  const mapRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);

  useEffect(() => {
    // Simulate map loading
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handlePointClick = (point) => {
    setSelectedPoint(point);
    onPointClick?.(point);
  };

  const closePopup = () => {
    setSelectedPoint(null);
  };

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg shadow-card overflow-hidden">
        <div className="h-96 bg-muted animate-pulse flex items-center justify-center">
          <div className="text-center">
            <Icon name="Map" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">Loading interactive map...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-card overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
            <Icon name="Route" size={20} className="text-primary" />
            Trail Route & Connected Points
          </h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="MapPin" size={16} />
            <span>{connectedPoints?.length} points of interest</span>
          </div>
        </div>
      </div>
      <div className="relative h-96" ref={mapRef}>
        {/* Google Maps Iframe */}
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title={`${trailData?.name} Trail Route`}
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${trailData?.coordinates?.lat},${trailData?.coordinates?.lng}&z=12&output=embed`}
          className="w-full h-full"
        />

        {/* Map Controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <Button
            variant="outline"
            size="icon"
            className="bg-card shadow-lg"
            onClick={() => window.open(`https://www.google.com/maps?q=${trailData?.coordinates?.lat},${trailData?.coordinates?.lng}&z=12`, '_blank')}
          >
            <Icon name="ExternalLink" size={16} />
          </Button>
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-card rounded-lg shadow-lg p-3">
          <h4 className="font-medium text-sm mb-2">Map Legend</h4>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full bg-primary"></div>
              <span>Trail Route</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full bg-accent"></div>
              <span>Cultural Sites</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full bg-success"></div>
              <span>Natural Points</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-3 h-3 rounded-full bg-warning"></div>
              <span>Tourism Services</span>
            </div>
          </div>
        </div>
      </div>
      {/* Connected Points List */}
      <div className="p-4 border-t border-border">
        <h4 className="font-medium text-sm mb-3">Connected Points of Interest</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {connectedPoints?.map((point) => (
            <button
              key={point?.id}
              onClick={() => handlePointClick(point)}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-colors duration-fast text-left"
            >
              <div className={`p-2 rounded-full ${
                point?.type === 'cultural' ? 'bg-accent/20 text-accent' :
                point?.type === 'natural'? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'
              }`}>
                <Icon 
                  name={
                    point?.type === 'cultural' ? 'Building2' :
                    point?.type === 'natural' ? 'Trees' : 'Bed'
                  } 
                  size={16} 
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{point?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{point?.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Point Details Popup */}
      {selectedPoint && (
        <div className="fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-heading font-semibold text-lg">{selectedPoint?.name}</h3>
              <Button variant="ghost" size="icon" onClick={closePopup}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="p-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Icon 
                    name={
                      selectedPoint?.type === 'cultural' ? 'Building2' :
                      selectedPoint?.type === 'natural' ? 'Trees' : 'Bed'
                    } 
                    size={16} 
                    className={
                      selectedPoint?.type === 'cultural' ? 'text-accent' :
                      selectedPoint?.type === 'natural'? 'text-success' : 'text-warning'
                    }
                  />
                  <span className="text-sm font-medium capitalize">{selectedPoint?.type} Site</span>
                </div>
                
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {selectedPoint?.fullDescription}
                </p>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="MapPin" size={14} />
                  <span>{selectedPoint?.location}</span>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="left"
                    onClick={() => window.open(`https://www.google.com/maps?q=${selectedPoint?.coordinates?.lat},${selectedPoint?.coordinates?.lng}`, '_blank')}
                  >
                    View on Map
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Info"
                    iconPosition="left"
                  >
                    More Info
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrailMap;